//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  UnifiedMetricsBenchmarksResponse,
  UnifiedMetricsCampaignsStatsResponse,
  UnifiedMetricsMetadataResponse,
} from '@bvengineering/metrics-gateway-service-types';

import { ITrackingParamGroup } from '@legacy-components/common/universal-image-actions/universal-image-actions-api-service';
import { BVSamplingCampaignId } from '@models/BVSamplingCampaignId';
import { BazaarvoiceClientId } from '@models/BazaarvoiceClientId';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import FileId from '@models/FileId';
import { DeetsItemId } from '@models/IDeetsModel';
import Interval from '@models/Interval';
import ItemId from '@models/ItemId';
import { SocialNetwork } from '@models/SocialNetwork';
import { UnifiedMetricsFormattedCampaignId } from '@models/UnifiedMetricsFormattedCampaignId';

export enum RequestContext {
  Touchpoint = 'Touchpoint',
  Facebook = 'Facebook',
  Instagram = 'Instagram',
}

export interface IStatsRequest {
  interval: string;
  ecommerceSessionType: EcommerceSessionType;
  sourceType?: InsightsSourceType[];
  testVariation?: string;
  sourceId?: string;
  displayType?: FanreelDisplayType;
  labels?: string[];
  comparisonInterval?: string;
  requestContext?: RequestContext;
}

export interface IStatsExportRequest extends IStatsRequest {
  sortKey?: SortKey;
  sortDirection?: SortDirection;
}

export interface IItemProductStatsRequest extends IStatsRequest {
  sortKey: SortKey;
  sortDirection: SortDirection;
  limit: number;
  offset: number;
  containerId?: string;
  excludeContainerId?: boolean;
  preferredNetworks?: string[];
}

// Campaign ids can come in a variety of formats, this endpoint will not modify or compose campaign ids, pass in the full campaign id here
export interface ReviewStatsRequest<T extends string = string> {
  interval?: Interval;
  ecommerceSessionType: EcommerceSessionType;
  campaignIds?: T[];
  productServiceIds?: string[];
  sortKey?: SortKey;
  sortDirection?: SortDirection;
  limit?: number;
  offset?: number;
  groupBy?: ReviewStatsGroupBy[];
}

export type SamplingCampaignReviewStatsRequest =
  ReviewStatsRequest<BVSamplingCampaignId>;

export interface EstimatedRevenueStatsRequest {
  campaignIds?: string[];
  ecommerceSessionType: EcommerceSessionType;
  groupBy?: EstimatedRevenueStatsGroupBy[];
  interval?: Interval;
  itemIds?: string[];
  limit?: number;
  offset?: number;
  productServiceIds?: string[];
  sortDirection?: SortDirection;
  sortKey?: SortKey;
  syndicationClientIds?: ClientId[];
}

export interface SyndicatedReviewEstimatedStatsRequest {
  destinationClientId?: ClientId;
  destinationBvClientId?: BazaarvoiceClientId;
  campaignId?: BVSamplingCampaignId;
  ecommerceSessionType: EcommerceSessionType;
  groupBy?: SyndicatedReviewEstimatedStatsGroupBy[];
  interval?: Interval;
  bvContentId?: string;
  limit?: number;
  offset?: number;
  clientProductServiceId?: string;
  destinationProductServiceId?: string;
  sortDirection?: SortDirection;
  sortKey?: SortKey;
}

export interface ILabelsRequest {
  interval: string;
  sourceType?: string;
  testVariation?: string;
}

export enum CampaignStatsGroupBy {
  SCHEDULE_TIMESTAMP = 'ScheduleTimestamp',
  CLIENT_ID = 'ClientId',
  SOURCE_TYPE = 'SourceType',
  SOURCE_ID = 'SourceId',
  DISPLAY_TYPE = 'DisplayType',
  DEVICE_TYPE = 'DeviceType',
  LOCALE = 'Locale',
  EXPERIMENT_ID = 'ExperimentId',
  VARIATION_ID = 'VariationId',
  ITEM_ID = 'ItemId',
  PRODUCT_SERVICE_ID = 'ProductServiceId',
  CONTAINER_ID = 'ContainerId',
  CURRENCY = 'Currency',
  TIMEZONE = 'Timezone',
  PARENT_PRODUCT_ID = 'ParentProductId',
  CAMPAIGN_ID = 'CampaignId',
  CREATOR_ID = 'CreatorId',
}

export const ReviewStatsGroupBy = {
  SCHEDULE_TIMESTAMP: 'scheduleTimestamp',
  CAMPAIGN_ID: 'campaignId',
  PRODUCT_SERVICE_ID: 'productServiceId',
} as const;

export type ReviewStatsGroupBy =
  (typeof ReviewStatsGroupBy)[keyof typeof ReviewStatsGroupBy];

export const SyndicatedReviewEstimatedStatsGroupBy = {
  CAMPAIGN_ID: 'campaignId',
  CLIENT_PRODUCT_SERVICE_ID: 'clientProductServiceId',
  DESTINATION_BV_CLIENT_ID: 'destinationBvClientId',
  DESTINATION_CLIENT_ID: 'destinationClientId',
  DESTINATION_PRODUCT_SERVICE_ID: 'destinationProductServiceId',
} as const;

export type SyndicatedReviewEstimatedStatsGroupBy =
  (typeof SyndicatedReviewEstimatedStatsGroupBy)[keyof typeof SyndicatedReviewEstimatedStatsGroupBy];

export const EstimatedRevenueStatsGroupBy = {
  CAMPAIGN_ID: 'campaignId',
  ITEM_ID: 'itemId',
  PRODUCT_SERVICE_ID: 'productServiceId',
  SYNDICATION_CLIENT_ID: 'syndicationClientId',
} as const;

export type EstimatedRevenueStatsGroupBy =
  (typeof EstimatedRevenueStatsGroupBy)[keyof typeof EstimatedRevenueStatsGroupBy];

export interface ICampaignAnalyticsStatsRequest extends IStatsRequest {
  creatorId?: string;
  campaignId?: UnifiedMetricsFormattedCampaignId;
  sortKey?: SortKey;
  sortDirection?: SortDirection;
  limit?: number;
  offset?: number;
  groupBy?: CampaignStatsGroupBy[];
}

export interface IContentAcquisitionResponse {
  acquiredCount: number;
  distributedCount: number;
  taggedCount: number;
}

export type CampaignAnalyticsStatsResponse = Omit<
  UnifiedMetricsCampaignsStatsResponse,
  'revenueCurrency'
> & { revenueCurrency: string };

export enum FanreelDisplayType {
  HOMEPAGE = 'Homepage',
  CATEGORY = 'Category',
  GALLERY = 'Gallery',
  PRODUCT_DETAIL = 'Pdp',
  CAMPAIGN = 'Campaign',
  INSTASHOP = 'Instashop',
  UNKNOWN = 'Unknown',
}

export enum EcommerceSessionType {
  IMPRESSION = 'Impression',
  HOVER = 'Hover',
  CLICK = 'Click',
  PRODUCT = 'Product',
}

export enum SortKey {
  VIEWS = 'Views',
  INTERACTIVE_VIEWS = 'InteractiveViews',
  EXTERNAL_CLICKS = 'ExternalClicks',
  CLICK_THROUGH_RATE = 'ClickthroughRate',
  FOLLOWERS = 'Followers',
  CONVERSION_RATE = 'ConversionRate',
  REVENUE = 'Revenue',
  PAGE_IMPRESSIONS_NO_CONTENT = 'PageImpressionsNoContent',
}

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum InsightsSourceType {
  REEL = 'REEL',
  L2B = 'L2B',
  L2B_V2 = 'L2B_V2',
  WIDGET = 'WIDGET', // Reveal
  TRIBUTARY = 'TRIBUTARY', // Showroom
  DYNAMIC_LANDING_PAGE = 'DYNAMIC_LANDING_PAGE',
  VTV = 'VTV',
  TRENDING_PRODUCTS = 'TRENDING_PRODUCTS',
  IG_STORY = 'IG_STORY',
  IG_PROMOTED_POST = 'IG_PROMOTED_POST',
  IG_SHOPPING = 'IG_SHOPPING',
  FB_POST = 'FB_POST',
  SHORTLINK = 'SHORTLINK',
  SHORTLINK_CMPGN = 'SHORTLINK_CMPGN',
}

export enum MetricsContainerType {
  LIKE_2_BUY = 'Like2Buy',
  FANREEL = 'Fanreel',
  REVEAL = 'Reveal',
  ALL = 'All',
  LANDING = 'DynamicLandingPage',
}

// The generated UnifiedMetricsMetadataResponse type incorrectly expects `currency` to be
// a Currency java type when the gateway is actually only returning an optional string.
export interface UnifiedMetricsMetadataResponseWithCurrencyOverride
  extends Omit<UnifiedMetricsMetadataResponse, 'currency'> {
  configuredTrackingParamGroups?: ITrackingParamGroup[];
  currency?: string;
  isGA4?: boolean;
}

// The response type of the LWS /showroom/stats/metadata endpoint
// Used to determine activation status
export interface ShowroomMetadataResponse {
  hasPosts?: boolean;
}

// The internal model for review stats returned from the metrics gateway
export interface ReviewStat {
  timestamp?: number; // unserialized timestamp, it is serialized below in the dateTimeIdentifier
  dateTimeIdentifier?: string; // date string formatted to MM-DD-YYYY
  campaignId?: string;
  productServiceId?: string; // productIds are saved in UMS as productServiceId as per UMS naming convention
  revenue: number;
}

export interface UnifiedMetricsReviewStatsResponse {
  reviewStats: ReviewStat[];
  totalCount: number;
  currency: string;
}

export interface SyndicatedReviewEstimatedStat {
  campaignId?: BVSamplingCampaignId;
  itemId?: ItemId;
  destinationClientId?: ClientId;
  destinationBvClientId?: BazaarvoiceClientId;
  estimatedRevenue: number;
  productServiceId?: string;
  dateTimeIdentifier?: string; // date for display in format M/d/yy
  timestamp?: number;
  bvContentId?: string;
  clientProductServiceId?: string;
  destinationProductServiceId?: string;
}

export interface UnifiedMetricsSyndicatedReviewEstimatedStatsResponse {
  syndicatedReviewEstimatedStats: SyndicatedReviewEstimatedStat[];
  totalCount: number;
  currency: string;
}

// TODO: PD-292848 - Consider moving this to SocialNetwork.ts for reusability.
// This type should also be investigated for proper alignment with metrics-gateway-service types
// which defines: "Pinterest" | "Facebook" | "Instagram" | "Twitter" | "Tumblr" | "Snapchat" | "TikTok" | "MediaImport"
// TODO: PD-292849 - Define MediaImport as an enum value to replace string literals used throughout the codebase
// (e.g., in TopPerformersMediaCard.tsx and other components)
export type MetricsNetworkType =
  | Exclude<SocialNetwork, SocialNetwork.Bazaarvoice | SocialNetwork.YouTube>
  | 'MediaImport';

export interface NetworkInfo {
  caption?: string;
  commentCount?: number;
  emailHash?: string;
  engagementRate?: number;
  followers?: number;
  fullName?: string;
  likeCount?: number;
  network: MetricsNetworkType;
  networkUrl?: string;
  reach?: number;
  username?: string;
  userProfileImageFileId?: FileId;
  userProfileImageUrl?: string;
  userProfileUrl?: string;
}

export enum MediaInfoType {
  ContributorInfo = 'NetworkInfo', // Backend maps ContributorInfo class to "NetworkInfo"
  ItemInfo = 'ItemInfo',
  ProductInfo = 'ProductInfo',
}

export interface ItemInfo {
  cuid: Cuid;
  deetsItemId: DeetsItemId;
  fileId: FileId;
  networkInfo?: NetworkInfo;
  timePosted?: number;
  touchpointUrl?: string;
  type: MediaInfoType.ItemInfo;
}

export interface ProductInfo {
  fileId?: FileId;
  productName?: string;
  productPrice?: number;
  productUrl?: string;
  type: MediaInfoType.ProductInfo;
}
export interface ContributorInfo {
  itemInfo?: ItemInfo;
  networkInfo?: NetworkInfo;
  type: MediaInfoType.ContributorInfo;
}

export type MediaInfoVariant = ItemInfo | ProductInfo | ContributorInfo;

interface UnifiedMetricsItemProductStatsResponse {
  clicks: number;
  clickThroughRate?: number;
  conversionRate?: number;
  ecommerceSessions: number;
  hovers: number;
  interactions: number;
  internalClicks: number;
  mediaInfo: MediaInfoVariant;
  pageImpressionNoContents: number;
  pageImpressions: number;
  revenue: number;
  taggedMedia?: string;
  totalCount: number;
  transactions: number;
  views: number;
}

export interface UnifiedMetricsItemProductStatsResponseItemInfo
  extends UnifiedMetricsItemProductStatsResponse {
  mediaInfo: ItemInfo;
}

export interface UnifiedMetricsItemProductStatsResponseProductInfo
  extends UnifiedMetricsItemProductStatsResponse {
  mediaInfo: ProductInfo;
}

export interface UnifiedMetricsItemProductStatsResponseContributorInfo
  extends UnifiedMetricsItemProductStatsResponse {
  mediaInfo: ContributorInfo;
}

// The internal model for estimated revenue stats returned from the metrics gateway
// TODO PD-293136: After confirming response from metrics gateway make tinytypes where necessary
export interface EstimatedRevenueStat {
  campaignId?: string;
  dateTimeIdentifier?: string; // formatted date time
  estimatedRevenue: number;
  itemId?: string;
  productServiceId?: string; // productId
  syndicationClientId?: string; // syndicationClientId
  timestamp?: number; // unserialized timestamp, it is serialized below in the dateTimeIdentifier
}

export interface UnifiedMetricsEstimatedRevenueStatsResponse {
  currency: string;
  estimatedRevenueStats: EstimatedRevenueStat[];
  totalCount: number;
}

export interface SingleUnifiedMetricsEstimatedRevenueStatsResponse {
  currency: string;
  estimatedRevenueStat?: EstimatedRevenueStat;
  totalCount: number;
}

// The generated UnifiedMetricsBenchmarksResponse type incorrectly expects `percentiles` to be an array of strings
// when the gateway is actually returning an array of numbers.
export interface UmsBenchmarkPercentiles {
  percentiles: number[];
}
export type UmsBenchmarksResponse = Omit<
  UnifiedMetricsBenchmarksResponse,
  'relatedClientStats'
> & { relatedClientStats: Record<string, UmsBenchmarkPercentiles> };
